import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { NotificationTypeResponse } from "../../codegen-api";
import { getBrandIconUrl } from "../../config/chains";
import { COLORS } from "../../constants/design/colors";
import checkmark from "../../assets/svg/liquidation-checkmark.svg";
import exclaimation from "../../assets/svg/exclaimation.svg";
import { AuthContext } from "../../contexts/AuthContext";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { NotificationContext } from "../../contexts/NotificationContext";
import { showBrowserNotification } from "../../utils/browserNotifications";
import { claimLiquidationToast } from "../../utils/liquidationToastDedupe";
import {
  getNotificationNavTarget,
  getNotificationSoundName,
  getNotificationSummary,
} from "../../utils/notifications";
import { useNotificationPreferences } from "../api/notifications/useNotificationPreferences";
import { isNotificationTypeMuted } from "../api/notifications/notificationMutes";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { useSFX } from "../useSFX";
import { useToast } from "../toast";
import {
  collectNewNotifications,
  createNotificationSeenState,
  getNotificationTxHash,
  isLiquidationNotification,
} from "./newNotifications";

const TOAST_AUTOCLOSE_MS = 5000;

// Per-brand icon, resolved by the active chain's logo folder.
const logo = getBrandIconUrl();

// Liquidation toasts get a dedicated glyph. Without an explicit icon the toast
// falls back to the generic success/error mark (which reads as the brand diamond)
// and a SUCCESS (green) status — wrong for a liquidation. Completed -> check,
// started/generic -> warning; all rendered on a negative (red) status.
const LIQUIDATION_TOAST_ICON: Partial<Record<NotificationTypeResponse, string>> = {
  [NotificationTypeResponse.LiquidationCompleted]: checkmark,
  [NotificationTypeResponse.LiquidationStarted]: exclaimation,
};

const LiquidationToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 50%;
  background: ${COLORS.negative.five};

  img {
    width: 100%;
    height: 100%;
  }
`;

// Mount once at the App root. Watches the notifications list from the
// NotificationContext and reacts to new entries with sound + toast + browser push.
export function useNewNotificationEffects() {
  const { account } = useContext(AuthContext);
  const { notifications, markAsRead } = useContext(NotificationContext);
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const { preferences } = useNotificationPreferences();
  const { addToast } = useToast();
  const { playSound } = useSFX();
  const { t } = useTranslation("app");
  const navigate = useNavigate();

  // Dedup by STABLE content key (tx_hash/fill_id), not the volatile UUID — the
  // backend re-emits the same event with a fresh id, which otherwise re-toasts a
  // day-old deposit on every poll. See getNotificationDedupeKey. Keys are kept
  // per account and bounded (see collectNewNotifications).
  const seenState = useRef(createNotificationSeenState());

  useEffect(() => {
    const newOnes = collectNewNotifications(
      seenState.current,
      account ?? undefined,
      notifications,
      Date.now() / 1000
    );
    if (newOnes.length === 0) return;

    newOnes.forEach((notif) => {
      // Respect per-type mutes — covers cases where backend permitted insert
      // but the user toggled mute after.
      // Liquidations are always on (ALWAYS_ON_NOTIFICATION_TYPES).
      if (isNotificationTypeMuted(preferences.muted_types, notif.notification_type)) return;

      const { title, body } = getNotificationSummary(
        notif,
        t,
        getMarketPrecision
      );

      // 1. Sound — only if user has sound enabled
      if (preferences.sound_enabled) {
        const sound = getNotificationSoundName(notif);
        playSound(sound, true);
      }

      // 2. Toast preview (always shown when tab is focused; harmless if user is elsewhere)
      //    A liquidation the on-chain listener already toasted for the same
      //    transaction is not toasted twice (XS-8).
      const liquidationIcon = LIQUIDATION_TOAST_ICON[notif.notification_type];
      const showToast =
        !isLiquidationNotification(notif) ||
        claimLiquidationToast(getNotificationTxHash(notif), "notification");
      if (showToast) {
        addToast(
          {
            status: liquidationIcon
              ? ToastStatusEnum.ERROR
              : ToastStatusEnum.SUCCESS,
            type: ToastEnum.SIMPLE,
            header: title,
            subheader: body,
            icon: liquidationIcon ? (
              <LiquidationToastIcon>
                <img src={liquidationIcon} alt="" />
              </LiquidationToastIcon>
            ) : undefined,
          },
          TOAST_AUTOCLOSE_MS
        );
      }

      // 3. Browser push (only fires when tab is hidden and permission granted)
      if (preferences.browser_push_enabled) {
        showBrowserNotification({
          title,
          body,
          icon: logo,
          tag: notif.id,
          onClick: () => {
            const target = getNotificationNavTarget(notif);
            if (target) navigate(target.path);
            if (notif.id) markAsRead(notif.id);
          },
        });
      }
    });
  }, [
    account,
    notifications,
    preferences.sound_enabled,
    preferences.browser_push_enabled,
    preferences.muted_types,
    addToast,
    playSound,
    navigate,
    markAsRead,
    getMarketPrecision,
    t,
  ]);
}
